/* eslint-disable react/prop-types */
import {useCallback, useContext, useEffect, useState} from 'react'
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom'

import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import Layout from '@adv-mt/mt-layout-basic'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import StepFormPlateRequired from '../../InstantOffer/components/StepFormPlateRequired/index.js'
import AppraiserForm from './components/AppraiserForm/index.js'
import AppraiserHero from './components/AppraiserHero/index.js'
import ListHowWeDoIt from './components/ListHowWeDoIt/index.js'
import PanelBenefits from './components/PanelBenefits/index.js'
import ParticularSellerBenefits from './components/ParticularSellerBenefits/index.js'
import RecommendedPriceCard from './components/RecommendedPriceCard/index.js'
import SellOptionsBlock from './components/SellOptionsBlock/index.js'
import {useVehicleAppraiserAnalyticsTrack} from './hooks/useVehicleAppraiserAnalyticsTrack.js'

const STEPS = {
  appraiserForm: 'appraiserForm',
  appraisal: 'appraisal',
  vehicleExtraDataForm: 'vehicleExtraDataForm'
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  })
}

const VehicleAppraiserPage = ({breadcrumb, helmet, pageName, sellVehicleConfigForm}) => {
  const {user} = useContext(AuthenticationContext)
  const {domain, i18n} = useContext(Context)
  const {trackAppraisalRequested, trackInstantOfferError, trackInstantOfferSuccess} =
    useVehicleAppraiserAnalyticsTrack()

  const config = domain.get('config')
  const {formErrorTypes} = config.get('form')

  const {VEHICLE_APPRAISER_PATH_PATTERN} = config.get('page')

  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const urlStep = searchParams.get('step') || STEPS.appraiserForm
  const {appraisal: locationAppraisal, hasError: locationHasError} = location.state || {}

  // Initialize from router state if it exists, otherwise empty
  const [appraisal, setAppraisal] = useState(locationAppraisal || {})
  const hasRecommendedPriceError = locationHasError || false

  // Guard: Validate that required data exists for protected steps
  const isAppraisalDataValid = appraisal && Object.keys(appraisal).length > 0
  const currentStep =
    (urlStep === STEPS.appraisal || urlStep === STEPS.vehicleExtraDataForm) && !isAppraisalDataValid
      ? STEPS.appraiserForm // Redirect to form if accessing protected steps without data
      : urlStep

  const navigateToStep = useCallback(
    (step, stateData = {}, options = {}) => {
      const {replace = false} = options
      const nextSearch = new URLSearchParams(searchParams)
      nextSearch.set('step', step)

      navigate(
        {pathname: VEHICLE_APPRAISER_PATH_PATTERN, search: `?${nextSearch.toString()}`},
        {replace, state: stateData}
      )
    },
    [navigate, searchParams, VEHICLE_APPRAISER_PATH_PATTERN]
  )

  // Sync URL if guard overrode the step
  useEffect(() => {
    if (urlStep !== currentStep && urlStep !== STEPS.appraiserForm) {
      navigateToStep(STEPS.appraiserForm, {}, {replace: true})
    }
  }, [urlStep, currentStep, navigateToStep])

  // flag to mangege visibility of InstantOfferFormHero depends on usecase get_simulated_instant_offer_seller_use_case
  const isC2BAppraisal = !!appraisal.c2bAppraisal

  const handleErrorFetchingRecommendedPrice = ({__FORM_STATE__, ...appraisal}) => {
    navigateToStep(STEPS.appraisal, {appraisal, hasError: true})
    scrollToTop()
  }

  const handleOnAppraise = ({__FORM_STATE__, ...appraisal}) => {
    domain
      .get('get_simulated_instant_offer_seller_use_case')
      .execute(appraisal)
      .then(async ([error, response]) => {
        let isTargetIO = false
        let completeAppraisal = appraisal

        if (error) {
          completeAppraisal = appraisal
        } else {
          isTargetIO = true
          completeAppraisal = {
            ...appraisal,
            c2bAppraisal: response.appraisal,
            title: response.title
          }
        }

        // Update local state with complete data
        setAppraisal(completeAppraisal)

        // Navigate with complete data in router state
        navigateToStep(STEPS.appraisal, {appraisal: completeAppraisal, hasError: false})

        trackAppraisalRequested({isTargetIO, vehicleData: appraisal})
      })
  }

  const isLogged = Boolean(user)

  const handleSubmitAppraiser = ({fields}) => {
    const {
      bodyTypeDoors,
      fuelId,
      km,
      makeId,
      makeName,
      modelId,
      modelName,
      postalCode,
      transmissionTypeId,
      versionId,
      versionName,
      year
    } = appraisal

    return domain
      .get('get_instant_offer_seller_use_case')
      .execute({
        bodyTypeDoors,
        fuelId,
        km,
        makeId,
        makeName,
        modelId,
        modelName,
        transmissionTypeId,
        versionId,
        versionName,
        year,
        ...fields,
        postalCode,
        email: user?.email,
        name: user?.name
      })
      .then(([error, response = {}]) => {
        if (error) {
          trackInstantOfferError({errorType: formErrorTypes.serverError})
          return {isError: true}
        } else {
          trackInstantOfferSuccess({ioId: response.instantOfferId})
          const destinationUrl = `/vende-a-concesionario/?ioId=${response.instantOfferId}&step=dealerSelectionForm&origin=vehicleAppraiser`
          navigate(destinationUrl)
        }
      })
  }

  return (
    <>
      <Helmet {...helmet} />
      <Layout.FullWidth linkFactory={LinkFactory}>
        <main className="mt-VehicleAppraiserPage">
          {currentStep === STEPS.appraiserForm && (
            <>
              <div className="mt-VehicleAppraiserPage-hero">
                <AppraiserHero />
              </div>
              <Layout.Container isFullWidth isMarginlessOnMobile>
                <div className="mt-VehicleAppraiserPage-formAndBenefitsSection">
                  <PanelBenefits />
                  <AppraiserForm
                    onAppraise={handleOnAppraise}
                    sellVehicleConfigForm={sellVehicleConfigForm}
                    isLogged={isLogged}
                    handleErrorFetchingRecommendedPrice={handleErrorFetchingRecommendedPrice}
                  />
                </div>
                <div className="mt-VehicleAppraiserPage-listHowWeDoItSection">
                  <ListHowWeDoIt />
                </div>
              </Layout.Container>
            </>
          )}
          {currentStep === STEPS.appraisal && (
            <Layout.Container isFullWidth isMarginlessOnMobile>
              <>
                <div className="mt-VehicleAppraiserPage-appraisalStepSection">
                  <div>
                    <h1 className="mt-VehicleAppraiserPage-appraisalStepTitle">
                      {hasRecommendedPriceError
                        ? i18n.t('LANDING_VEHICLE_APPRAISER.APPRAISAL.TITLE_NO_RESULTS')
                        : i18n.t('LANDING_VEHICLE_APPRAISER.APPRAISAL.TITLE')}
                    </h1>

                    {hasRecommendedPriceError && (
                      <p>{i18n.t('LANDING_VEHICLE_APPRAISER.APPRAISAL.NO_RESULTS_MESSAGE')}</p>
                    )}

                    <RecommendedPriceCard
                      {...appraisal}
                      currentUser={user}
                      hasRecommendedPriceError={hasRecommendedPriceError}
                    />
                  </div>
                  {isC2BAppraisal && !hasRecommendedPriceError ? (
                    <SellOptionsBlock
                      {...appraisal}
                      onNavigateToVehicleExtraDataFormStep={() =>
                        navigateToStep(STEPS.vehicleExtraDataForm, {appraisal, hasError: hasRecommendedPriceError})
                      }
                    />
                  ) : (
                    <ParticularSellerBenefits {...appraisal} />
                  )}

                  <ListHowWeDoIt />
                </div>
                {isC2BAppraisal && (
                  <div className="mt-VehicleAppraiserPage-breadcrumb">
                    <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />
                  </div>
                )}
              </>
            </Layout.Container>
          )}
          {currentStep === STEPS.vehicleExtraDataForm && (
            <StepFormPlateRequired
              instantOfferAppraisal={appraisal?.c2bAppraisal?.priceRecommended.value}
              blockPostalCodeField={true}
              onSubmit={handleSubmitAppraiser}
              shouldUseAppraiserConfigForm
              initialData={{phone: user?.phone}}
            />
          )}
        </main>
      </Layout.FullWidth>
      <TrackingPageView pageName={pageName} />
    </>
  )
}

VehicleAppraiserPage.displayName = 'VehicleAppraiserPage'

VehicleAppraiserPage.propTypes = {}

export default VehicleAppraiserPage
